import dayjs from "dayjs";

/** PAS RADE / mailing report display names (match the downloaded workbooks). */
export function formatPasRadeBrandName(name) {
  if (!name) return name;
  const key = String(name).replace(/\s+/g, " ").trim().toLowerCase();
  if (key === "house of man" || key === "houseofman" || key === "hom") return "House of Man";
  if (key === "upower" || key === "u-power") return "U-Power";
  return name;
}

export function extractApiList(res) {
  if (res == null) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.results)) return res.results;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

/** idToNameMap: brand id -> brand_name (e.g. from selected brands in Redux) */
export function resolveBrandLabel(item, idToNameMap = {}) {
  const named =
    (typeof item.brand_name === "string" && item.brand_name.trim()) ||
    (typeof item.credentials_name === "string" && item.credentials_name.trim());
  if (named) return named;

  const bid =
    item.brand_id ??
    item.brand?.id ??
    (typeof item.brand === "number" || typeof item.brand === "string" ? item.brand : null) ??
    item.gateway?.brand ??
    item.credentials?.brand ??
    item.gateway_credentials?.brand ??
    item.file?.brand ??
    null;

  if (bid != null && bid !== "") {
    const n = Number(bid);
    const key = Number.isNaN(n) ? String(bid) : n;
    if (idToNameMap[key] != null) return idToNameMap[key];
    if (idToNameMap[String(key)] != null) return idToNameMap[String(key)];
  }

  return Object.values(idToNameMap)[0] || "";
}

/**
 * Dashboard grouping label: **brand id → selected brand name** first, then API brand_name (skip NOTFOUND junk),
 * then credential name. Use for `client` on normalized rows so drill-downs match Redux / global brand list.
 */
export function resolveDashboardBrandClient(item, idToNameMap = {}) {
  if (!item || typeof item !== "object") return "";

  let bid =
    item.brand_id ??
    item.brand?.id ??
    (typeof item.brand === "number" ? item.brand : null) ??
    null;
  if (bid == null && typeof item.brand === "string" && /^\d+$/.test(item.brand.trim())) {
    bid = item.brand.trim();
  }
  if (bid == null && item.credentials?.brand != null) {
    const b = item.credentials.brand;
    bid = typeof b === "object" ? b.id : b;
  }
  if (bid == null && item.gateway?.brand != null) {
    const b = item.gateway.brand;
    bid = typeof b === "object" ? b.id : b;
  }
  if (bid == null && item.gateway_credentials?.brand != null) {
    const b = item.gateway_credentials.brand;
    bid = typeof b === "object" ? b.id : b;
  }
  if (bid == null && item.file?.brand != null) {
    const b = item.file.brand;
    bid = typeof b === "object" ? b.id : b;
  }

  if (bid != null && bid !== "") {
    const n = Number(bid);
    const key = Number.isNaN(n) ? String(bid) : n;
    const mapped = idToNameMap[key] ?? idToNameMap[String(key)];
    if (mapped) return mapped;
  }

  const rawName = (typeof item.brand_name === "string" && item.brand_name.trim()) || "";
  if (rawName && !/^notfound$/i.test(rawName)) return rawName;

  const cred =
    (typeof item.credentials_name === "string" && item.credentials_name.trim()) ||
    (typeof item.credential_name === "string" && item.credential_name.trim()) ||
    "";
  if (cred) return cred;

  return "";
}

function relCount(v) {
  return Array.isArray(v) ? v.length : 0;
}

/** Prefer display email when Sublytics uses noemail.com placeholder; check address JSON if present. */
function pickSublyticsDisplayEmail(order, cust, apiEmail) {
  const fromApi = typeof apiEmail === "string" ? apiEmail.trim() : "";
  const isPlaceholder = (e) => {
    if (!e) return true;
    const x = e.toLowerCase();
    return x.includes("noemail") || x.endsWith("@noemail.com") || x.endsWith("@noemail.org");
  };
  if (fromApi && !isPlaceholder(fromApi)) return fromApi;
  for (const addr of [order.customer_address_billing, order.customer_address_shipping]) {
    if (!addr || typeof addr !== "object") continue;
    const alt = (addr.email || addr.email_address || addr.customer_email || "").trim();
    if (alt && !isPlaceholder(alt)) return alt;
  }
  const fromCust = (cust.email || cust.email_address || "").trim();
  if (fromCust && !isPlaceholder(fromCust)) return fromCust;
  return fromApi || fromCust || "";
}

/** Last successful capture/sale in Sublytics JSON (matches backend total_transaction_amount logic). */
function pickSublyticsCaptureTx(transactions) {
  if (!Array.isArray(transactions) || !transactions.length) return null;
  for (let i = transactions.length - 1; i >= 0; i--) {
    const t = transactions[i];
    const txType = String(t.transaction_type_id ?? t.type ?? "").toLowerCase();
    if (txType !== "capture" && txType !== "sale") continue;
    const declined = String(t.transaction_declined ?? "").toLowerCase();
    if (declined && declined !== "approved" && declined !== "success") continue;
    return t;
  }
  return null;
}

/** Skip tracking/custom values that are clearly URLs (not a store line label). */
function looksLikeTrackingUrl(s) {
  const t = String(s || "").trim();
  if (!t) return true;
  return /^https?:\/\//i.test(t) || /^www\./i.test(t);
}

/**
 * Human-readable brand/credential for Sublytics UI. Do **not** use tracking1–5 here — those are
 * often internal campaign/source codes (e.g. HYL080), not store names.
 */
export function pickSublyticsDisplayClient(order, brandFallback = "") {
  if (!order || typeof order !== "object") return brandFallback || "";
  const bn = (typeof order.brand_name === "string" && order.brand_name.trim()) || "";
  if (bn) return bn;
  const cred =
    (typeof order.credentials_name === "string" && order.credentials_name.trim()) ||
    (typeof order.credential_name === "string" && order.credential_name.trim()) ||
    "";
  if (cred) return cred;
  return brandFallback || "";
}

/** Sublytics tracking1–5 values to try when matching TigerTech / gateways (not for table labels). */
function collectSublyticsTrackingTokens(order) {
  if (!order || typeof order !== "object") return [];
  const out = [];
  const push = (x) => {
    const t = (x || "").trim();
    if (t && !looksLikeTrackingUrl(t) && !out.includes(t)) out.push(t);
  };
  for (let i = 1; i <= 5; i++) {
    const v = order[`tracking${i}`];
    const t = typeof v === "string" ? v.trim() : String(v ?? "").trim();
    push(t);
  }
  return out;
}

/** TigerTech custom_1–5 for cross-source id matching only. */
function collectTigerTechCustomTokens(sale) {
  if (!sale || typeof sale !== "object") return [];
  const out = [];
  const push = (x) => {
    const t = (x || "").trim();
    if (t && !looksLikeTrackingUrl(t) && !out.includes(t)) out.push(t);
  };
  for (let i = 1; i <= 5; i++) {
    const v = sale[`custom_${i}`];
    const t = typeof v === "string" ? v.trim() : String(v ?? "").trim();
    push(t);
  }
  return out;
}

/**
 * TigerTech custom_1–5 often holds SKUs / campaign IDs (BLG-030-01, COLV1NF, HYL080), not store names.
 * Values with spaces ("U Power", "House of Man") are treated as real line labels. Otherwise skip codes
 * and fall back to API brand_name so the dashboard does not sprout hundreds of fake “brands”.
 */
function looksLikeTigerTechCampaignCode(t) {
  const s = String(t || "").trim();
  if (!s || looksLikeTrackingUrl(s)) return true;
  if (/^\d{5,}$/.test(s)) return true;

  // Store / client lines in reports are almost always multi-word; never treat those as file codes.
  if (/\s/.test(s)) return false;

  // Hyphenated SKU / segment codes: BLG-030-01, COL-363A, HHC-002F, CEP-42A
  if (/^[A-Z]{2,6}(?:-[A-Z0-9]{1,10}){1,6}$/i.test(s)) return true;

  // Letter prefix + digits: HYL080, EVR009, ERO071
  if (/^[A-Z]{2,5}\d{2,8}[A-Z0-9]*$/i.test(s) && s.length <= 16) return true;

  // Compact alnum with digit(s), no spaces: COLV1NF, GPCAT1, COM-RET4 style without hyphen handled above
  if (/^[A-Z0-9]{4,16}$/i.test(s) && /\d/.test(s) && /[A-Za-z]/.test(s)) return true;

  // Short consonant-heavy tokens (legacy)
  if (/^[A-Z0-9]{4,12}$/i.test(s) && !/[aeiou]/i.test(s)) return true;

  return false;
}

/** Primary label for TigerTech rows: first custom that looks like a name, else company brand. */
export function pickTigerTechDisplayClient(sale, brandFallback = "") {
  if (!sale || typeof sale !== "object") return brandFallback || "";
  for (let i = 1; i <= 5; i++) {
    const v = sale[`custom_${i}`];
    const t = typeof v === "string" ? v.trim() : String(v ?? "").trim();
    if (!t || looksLikeTrackingUrl(t)) continue;
    if (!looksLikeTigerTechCampaignCode(t)) return t;
  }
  const bn = (typeof sale.brand_name === "string" && sale.brand_name.trim()) || "";
  if (bn) return bn;
  return brandFallback || "";
}

/** WooCommerce: prefer API brand_name, then optional meta hints, then fallback. */
function pickWordPressLineClient(order, brandFallback = "") {
  if (!order || typeof order !== "object") return brandFallback || "";
  const bn = (typeof order.brand_name === "string" && order.brand_name.trim()) || "";
  if (bn) return bn;
  const meta = order.meta_data;
  if (Array.isArray(meta)) {
    for (const m of meta) {
      if (!m || typeof m !== "object") continue;
      const key = String(m.key || "").toLowerCase();
      const rawVal = m.value;
      let val = "";
      if (typeof rawVal === "string") val = rawVal.trim();
      else if (rawVal != null && typeof rawVal !== "object") val = String(rawVal).trim();
      if (!val || looksLikeTrackingUrl(val)) continue;
      if (
        /_?tracking|sub_?brand|store_?line|client_?line|division|channel|sales_?line|lob|brand_?line/i.test(
          key
        )
      ) {
        return val;
      }
    }
  }
  return brandFallback || "";
}

const SHARED_GATEWAY_BRANDS = {
  "House Of Man": ["Max Vital"],
  "House of Man": ["Max Vital"],
};

/** Sublytics / WordPress rows: display client plus credential/company/tracking aliases for cross-source keys. */
function subOrderMatchClientAliases(rec) {
  const order = rec?.rawData || {};
  const line = (rec?.client || "").trim();
  const cred = (typeof order.credentials_name === "string" && order.credentials_name.trim()) || "";
  const bn = (typeof order.brand_name === "string" && order.brand_name.trim()) || "";
  const out = [];
  const push = (x) => {
    const t = (x || "").trim();
    if (t && !out.includes(t)) out.push(t);
  };
  push(line);
  push(cred);
  push(bn);
  if (rec?.source === "Sublytics") {
    for (const t of collectSublyticsTrackingTokens(order)) push(t);
  }
  for (const alias of out.slice()) {
    const shared = SHARED_GATEWAY_BRANDS[alias];
    if (shared) shared.forEach((s) => push(s));
  }
  return out;
}

function tigerTechRowMatchClientAliases(r) {
  const sale = r?.rawData || {};
  const company = (typeof sale.brand_name === "string" && sale.brand_name.trim()) || "";
  const out = [];
  const push = (x) => {
    const t = (x || "").trim();
    if (t && !out.includes(t)) out.push(t);
  };
  push((r?.client || "").trim());
  push(company);
  for (const t of collectTigerTechCustomTokens(sale)) push(t);
  return out;
}

/**
 * Normalize a Sublytics order to the unified sales record shape.
 * List API exposes customer_email / customer_name; transactions use transaction_type_id not type.
 */
export function sublyticsOrderAmount(order) {
  const offers = Array.isArray(order?.order_offers) ? order.order_offers : [];
  let product = 0;
  let shipping = 0;
  offers.forEach((offer) => {
    const price = parseFloat(offer?.order_offer_price) || 0;
    const qty = parseInt(offer?.order_offer_quantity, 10) || 1;
    shipping += parseFloat(offer?.order_offer_shipping) || 0;
    product += price * qty;
  });
  const offerFull = product + shipping;

  const lastCapture = pickSublyticsCaptureTx(order?.transactions || []);
  const txTotal = parseFloat(lastCapture?.transaction_total ?? lastCapture?.amount ?? 0);
  const txShip = parseFloat(lastCapture?.transaction_shipping ?? 0);
  if (Number.isFinite(txTotal) && txTotal > 0) {
    if (txShip > 0 && Math.abs(txTotal - product) <= 0.05) return txTotal + txShip;
    if (shipping > 0 && txTotal + 0.02 < offerFull && Math.abs(txTotal - product) <= 0.51) {
      return txTotal + shipping;
    }
    return txTotal;
  }
  const apiTotal = parseFloat(order?.total_transaction_amount);
  if (Number.isFinite(apiTotal) && apiTotal > 0) {
    if (shipping > 0 && apiTotal + 0.02 < offerFull && Math.abs(apiTotal - product) <= 0.51) {
      return apiTotal + shipping;
    }
    return apiTotal;
  }
  return offerFull;
}

export const normalizeSublyticsOrder = (order, brandNameMap = {}) => {
  const transactions = order.transactions || [];
  const lastCapture = pickSublyticsCaptureTx(transactions);
  const amount = sublyticsOrderAmount(order);

  const cust = order.customer && typeof order.customer === "object" ? order.customer : {};
  const apiEmail =
    (typeof order.customer_email === "string" && order.customer_email.trim()) ||
    (typeof cust.email_address === "string" && cust.email_address.trim()) ||
    (typeof cust.email === "string" && cust.email.trim()) ||
    "";
  const customerEmail = pickSublyticsDisplayEmail(order, cust, apiEmail);
  const customerName =
    (typeof order.customer_name === "string" && order.customer_name.trim()) ||
    `${cust.first_name || cust.fname || ""} ${cust.last_name || cust.lname || ""}`.trim();

  const txId =
    lastCapture?.transaction_id ??
    lastCapture?.merchant_transaction_id ??
    lastCapture?.id;

  const client =
    resolveDashboardBrandClient(order, brandNameMap) ||
    pickSublyticsDisplayClient(order, Object.values(brandNameMap)[0] || "");

  return {
    _id: `sublytics-${order.id}`,
    source: "Sublytics",
    client,
    order_id: order.id,
    transaction_id: txId != null && txId !== "" ? String(txId) : "",
    customer_email: customerEmail,
    customer_name: customerName,
    amount: typeof amount === "number" ? amount : parseFloat(amount) || 0,
    status: lastCapture ? "Captured" : "Pending",
    date: order.date_created
      ? dayjs(order.date_created).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: order.date_created || "",
    matched_tigertech: !!(
      order.respective_tiger_tech_order ||
      order.respective_tiger_tech_order_id
    ),
    matched_gateway:
      relCount(order.real_auth_transactions) +
        relCount(order.real_stripe_transactions) +
        relCount(order.real_nmi_transactions) >
      0,
    platform: "Sublytics",
    rawData: order,
  };
};

const GATEWAY_SOURCES = new Set(["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Reliable Solutions", "Stripe", "Elavon"]);

const OFFLINE_METHODS = new Set(["cash", "check", "money order", "money_order", "moneyorder"]);
const PM_ID_MAP = { "1": "credit card", "2": "check", "5": "cash" };

export const normalizePaymentMethodId = (pmId) => {
  const raw = String(pmId ?? "").trim().toLowerCase();
  return PM_ID_MAP[raw] || raw;
};

export const isSublyticsCash = (pmId, pmName = "") => {
  const pid = normalizePaymentMethodId(pmId);
  const name = String(pmName || "").trim().toLowerCase().replace(/[_-]+/g, " ");
  const tokens = new Set(name.split(/\s+/).filter(Boolean));
  if (pid === "check" || tokens.has("check")) return false;
  if (pid === "cash") return true;
  return tokens.has("cash") && !name.includes("cashapp");
};

export const isOfflineMethod = (rec) => {
  const raw = typeof rec === "string" ? null : rec?.rawData || rec || {};
  const pmId = normalizePaymentMethodId(typeof rec === "string" ? "" : raw.payment_method_id);
  if (pmId === "cash" || pmId === "check") return true;
  const pm = typeof rec === "string" ? rec : normName(raw.payment_method || raw.payment_method_title || "");
  if (!pm) return false;
  if (OFFLINE_METHODS.has(pm)) return true;
  return ["check", "money order"].some((kw) => pm.includes(kw)) || isSublyticsCash(pmId, pm);
};

const paymentMethodBits = (rec) => {
  const raw = rec?.rawData || {};
  const pmId = normalizePaymentMethodId(raw.payment_method_id ?? rec?.payment_method_id ?? "");
  const pmName = String(
    raw.payment_method ?? raw.payment_method_title ?? rec?.payment_method ?? rec?.payment_method_title ?? ""
  ).trim().toLowerCase();
  return { pmId, pmName };
};

/** Cash / Check / Gateway for accountants. Payment method wins over gateway match. */
export const describePaymentChannel = (rec) => {
  if (!rec) return "";
  const src = rec.source || "";
  if (src === "Check") return "Check";
  if (src === "Cash") return "Cash";
  if (GATEWAY_SOURCES.has(src)) return "Gateway";
  if (src === "Sublytics" || src === "Wordpress") {
    const { pmId, pmName } = paymentMethodBits(rec);
    if (pmId === "check" || pmName.includes("check")) return "Check";
    if (isSublyticsCash(pmId, pmName)) return "Cash";
    if (pmName.includes("money order") || pmName.includes("money_order") || pmId === "money order") return "Check";
    if (isOfflineMethod(rec)) return "Check";
    return "Gateway";
  }
  return "";
};

// --- Normalization helpers (Step 0) ---
const normEmail = (e) => (e || "").trim().toLowerCase();
const normAmount = (a) => Math.round((Number(a) || 0) * 100) / 100;
const normTxnId = (t) => String(t ?? "").trim().toUpperCase().replace(/^-+|-+$/g, "");
const normOrderId = (o) => String(o ?? "").trim().replace(/^[^a-zA-Z0-9]+/, "");
const normName = (n) => (n || "").trim().toLowerCase().replace(/\s+/g, " ");
const normDate = (d) => (d ? dayjs(d).utc?.() ?? dayjs(d) : null);
const isPlaceholderEmail = (e) => {
  const s = normEmail(e);
  if (!s || s.length < 3) return true;
  if (s === "noemail@noemail.com" || s === "noemail@noemail.org") return true;
  return false;
};

const AMOUNT_VARIANCE_THRESHOLD = 0.50;
const DATE_TOLERANCE_HOURS = 48;

const DEAD_STATUSES = new Set(["declined", "voided", "error", "expired"]);
const isUnmatchableGateway = (gw) => {
  const amt = normAmount(gw.amount);
  const st = (gw.status || "").trim().toLowerCase();
  if (amt === 0 && DEAD_STATUSES.has(st)) return true;
  if (st === "declined") return true;
  return false;
};

/**
 * 6-priority reconciliation: match each Sublytics/WordPress order against gateway records.
 * Assigns reconciliation_status + reconciliation_reason on every order-source row.
 * Maintains a matchedGatewayRecordIds Set so no gateway record is double-matched.
 */
export function enrichSublyticsWithGatewayMatchHint(records) {
  if (!records?.length) return records;

  const gwRows = records.filter((r) => GATEWAY_SOURCES.has(r.source));
  if (!gwRows.length) {
    return records.map((r) => {
      if (r.source !== "Sublytics" && r.source !== "Wordpress") return r;
      if (r.matched_gateway) return { ...r, reconciliation_status: "matched", reconciliation_reason: "db_fk_linked" };
      if (isOfflineMethod(r)) return { ...r, reconciliation_status: "settled_offline", reconciliation_reason: "offline_payment_method" };
      return { ...r, reconciliation_status: "missing_in_gateway", reconciliation_reason: "no_gateway_data_loaded" };
    });
  }

  // Build gateway lookup indices (keyed by client alias)
  const gwByTxnId = new Map();   // "CLIENT::TXNID" -> [gwRow, ...]
  const gwByOrderId = new Map();  // "CLIENT::ORDERID" -> [gwRow, ...]
  const gwByEmailAmt = new Map(); // "CLIENT::email::amount" -> [gwRow, ...]
  const gwByEmail = new Map();    // "CLIENT::email" -> [gwRow, ...]
  const gwByNameAmtDate = [];     // flat array for Priority 6 scan

  for (const gw of gwRows) {
    const dead = isUnmatchableGateway(gw);
    const clients = [gw.client || ""].filter(Boolean);
    const tid = normTxnId(gw.transaction_id);
    const oid = normOrderId(gw.order_id);
    const em = normEmail(gw.customer_email);
    const amt = normAmount(gw.amount);
    const nm = normName(gw.customer_name);
    const dt = normDate(gw.raw_date);

    for (const c of clients) {
      if (tid) {
        const k = `${c}::${tid}`;
        if (!gwByTxnId.has(k)) gwByTxnId.set(k, []);
        gwByTxnId.get(k).push(gw);
      }
      if (oid && oid !== tid) {
        const k = `${c}::${oid}`;
        if (!gwByOrderId.has(k)) gwByOrderId.set(k, []);
        gwByOrderId.get(k).push(gw);
      }
      if (!dead && !isPlaceholderEmail(em)) {
        const eaKey = `${c}::${em}::${amt}`;
        if (!gwByEmailAmt.has(eaKey)) gwByEmailAmt.set(eaKey, []);
        gwByEmailAmt.get(eaKey).push(gw);

        const eKey = `${c}::${em}`;
        if (!gwByEmail.has(eKey)) gwByEmail.set(eKey, []);
        gwByEmail.get(eKey).push(gw);
      }
    }
    if (!dead) gwByNameAmtDate.push({ gw, clients, nm, amt, dt });
  }

  const matchedGatewayIds = new Set();

  function tryClaimGateway(candidates) {
    const available = candidates.filter((g) => !matchedGatewayIds.has(g._id));
    if (available.length === 0) return null;
    const viable = available.filter((g) => !isUnmatchableGateway(g));
    if (viable.length > 0) return viable[0];
    return null;
  }

  function runPriorityChain(r) {
    // Already matched via DB FK
    if (r.matched_gateway) return { ...r, reconciliation_status: "matched", reconciliation_reason: "db_fk_linked" };

    // Offline payment methods — no gateway match expected
    const pm = normName(r.rawData?.payment_method || r.rawData?.payment_method_title || "");
    if (OFFLINE_METHODS.has(pm)) return { ...r, reconciliation_status: "settled_offline", reconciliation_reason: "offline_payment_method" };

    const aliases = subOrderMatchClientAliases(r);
    const sTxnId = normTxnId(r.transaction_id);
    const sOrderId = normOrderId(r.order_id);
    const sEmail = normEmail(r.customer_email);
    const sAmt = normAmount(r.amount);
    const sName = normName(r.customer_name);
    const sDate = normDate(r.raw_date);

    // Priority 1: Transaction ID (check both txnId→txnId AND txnId→orderId for cross-system matching)
    if (sTxnId) {
      for (const c of aliases) {
        const candidates = gwByTxnId.get(`${c}::${sTxnId}`) || gwByOrderId.get(`${c}::${sTxnId}`);
        if (!candidates) continue;
        const gw = tryClaimGateway(candidates);
        if (gw) {
          matchedGatewayIds.add(gw._id);
          const variance = Math.abs(sAmt - normAmount(gw.amount));
          if (variance > AMOUNT_VARIANCE_THRESHOLD) {
            return { ...r, matched_gateway: true, reconciliation_status: "matched_with_variance", reconciliation_reason: "amount_mismatch", reconciliation_match_priority: 1, reconciliation_gateway_id: gw._id };
          }
          return { ...r, matched_gateway: true, reconciliation_status: "matched", reconciliation_reason: "transaction_id", reconciliation_match_priority: 1, reconciliation_gateway_id: gw._id };
        }
        if (candidates.length > 0) {
          return { ...r, reconciliation_status: "duplicate_candidate", reconciliation_reason: "duplicate_records_found", reconciliation_match_priority: 1 };
        }
      }
    }

    // Priority 2: Order ID (check orderId→orderId, orderId→txnId, AND orderId→orderId)
    if (sOrderId) {
      for (const c of aliases) {
        const candidates = gwByOrderId.get(`${c}::${sOrderId}`) || gwByTxnId.get(`${c}::${sOrderId}`);
        if (!candidates) continue;
        const gw = tryClaimGateway(candidates);
        if (gw) {
          matchedGatewayIds.add(gw._id);
          const variance = Math.abs(sAmt - normAmount(gw.amount));
          if (variance > AMOUNT_VARIANCE_THRESHOLD) {
            return { ...r, matched_gateway: true, reconciliation_status: "matched_with_variance", reconciliation_reason: "amount_mismatch", reconciliation_match_priority: 2, reconciliation_gateway_id: gw._id };
          }
          return { ...r, matched_gateway: true, reconciliation_status: "matched", reconciliation_reason: "order_id", reconciliation_match_priority: 2, reconciliation_gateway_id: gw._id };
        }
      }
    }

    // Priority 3: Email + Amount (exact)
    if (!isPlaceholderEmail(sEmail)) {
      for (const c of aliases) {
        const candidates = gwByEmailAmt.get(`${c}::${sEmail}::${sAmt}`);
        if (!candidates) continue;
        let available = candidates.filter((g) => !matchedGatewayIds.has(g._id));
        if (available.length > 1) {
          const nonRefDecl = available.filter((g) => g.status !== "Refunded" && g.status !== "Declined");
          if (nonRefDecl.length >= 1) available = nonRefDecl;
        }
        if (available.length > 1 && sDate) {
          available.sort((a, b) => {
            const da = normDate(a.raw_date), db = normDate(b.raw_date);
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            return Math.abs(sDate.diff(da, "minute")) - Math.abs(sDate.diff(db, "minute"));
          });
          available = [available[0]];
        }
        if (available.length === 1) {
          matchedGatewayIds.add(available[0]._id);
          return { ...r, matched_gateway: true, reconciliation_status: "matched", reconciliation_reason: "email_amount", reconciliation_match_priority: 3, reconciliation_gateway_id: available[0]._id };
        }
      }

      // Priority 4: Email + Amount + Date within 48h
      if (sDate) {
        for (const c of aliases) {
          const emailPool = gwByEmail.get(`${c}::${sEmail}`);
          if (!emailPool) continue;
          let withinWindow = emailPool.filter((g) => {
            if (matchedGatewayIds.has(g._id)) return false;
            if (normAmount(g.amount) !== sAmt) return false;
            const gDate = normDate(g.raw_date);
            if (!gDate) return false;
            return Math.abs(sDate.diff(gDate, "hour")) <= DATE_TOLERANCE_HOURS;
          });
          if (withinWindow.length > 1) {
            const nonRefDecl = withinWindow.filter((g) => g.status !== "Refunded" && g.status !== "Declined");
            if (nonRefDecl.length >= 1) withinWindow = nonRefDecl;
          }
          if (withinWindow.length > 1) {
            withinWindow.sort((a, b) => {
              const da = normDate(a.raw_date), db = normDate(b.raw_date);
              if (!da && !db) return 0;
              if (!da) return 1;
              if (!db) return -1;
              return Math.abs(sDate.diff(da, "minute")) - Math.abs(sDate.diff(db, "minute"));
            });
            withinWindow = [withinWindow[0]];
          }
          if (withinWindow.length === 1) {
            matchedGatewayIds.add(withinWindow[0]._id);
            return { ...r, matched_gateway: true, reconciliation_status: "matched", reconciliation_reason: "email_amount_date", reconciliation_match_priority: 4, reconciliation_gateway_id: withinWindow[0]._id };
          }
        }
      }

      // Priority 5: Email only — claim the gateway when exactly 1 candidate
      for (const c of aliases) {
        const emailPool = gwByEmail.get(`${c}::${sEmail}`);
        if (!emailPool) continue;
        let available = emailPool.filter((g) => !matchedGatewayIds.has(g._id));
        if (available.length > 1) {
          const nonRefDecl = available.filter((g) => g.status !== "Refunded" && g.status !== "Declined");
          if (nonRefDecl.length >= 1) available = nonRefDecl;
        }
        if (available.length === 1) {
          matchedGatewayIds.add(available[0]._id);
          return { ...r, matched_gateway: true, reconciliation_status: "matched", reconciliation_reason: "email_only", reconciliation_match_priority: 5, reconciliation_gateway_id: available[0]._id };
        }
        if (available.length > 1) {
          return { ...r, reconciliation_status: "partially_matched", reconciliation_reason: "missing_transaction_id", reconciliation_match_priority: 5 };
        }
      }
    }

    // Priority 6: Name + Amount + Date within 48h
    if (sName && sDate) {
      const candidates = gwByNameAmtDate.filter(({ gw, clients, nm, amt, dt }) => {
        if (matchedGatewayIds.has(gw._id)) return false;
        if (amt !== sAmt || nm !== sName) return false;
        if (!dt) return false;
        if (Math.abs(sDate.diff(dt, "hour")) > DATE_TOLERANCE_HOURS) return false;
        return clients.some((c) => aliases.includes(c));
      });
      if (candidates.length === 1) {
        matchedGatewayIds.add(candidates[0].gw._id);
        return { ...r, reconciliation_status: "partially_matched", reconciliation_reason: "name_amount_date", reconciliation_match_priority: 6 };
      }
    }

    // No match found
    return { ...r, reconciliation_status: "missing_in_gateway", reconciliation_reason: "missing_transaction_id" };
  }

  // Process order-source rows first (Sublytics, then WordPress), preserving original order for everything else
  const orderRows = [];
  const otherRows = [];
  const indexMap = new Map();

  records.forEach((r, i) => {
    if (r.source === "Sublytics" || r.source === "Wordpress") {
      orderRows.push({ r, i });
    } else {
      otherRows.push({ r, i });
    }
    indexMap.set(i, r);
  });

  // Sublytics first (canonical), then WordPress
  orderRows.sort((a, b) => {
    if (a.r.source === "Sublytics" && b.r.source !== "Sublytics") return -1;
    if (a.r.source !== "Sublytics" && b.r.source === "Sublytics") return 1;
    return 0;
  });

  for (const { r, i } of orderRows) {
    indexMap.set(i, runPriorityChain(r));
  }

  return records.map((_, i) => indexMap.get(i));
}

export function enrichSublyticsWithTigerTechMatches(records) {
  if (!records?.length) return records;
  const ttRows = records.filter((r) => r.source === "TigerTech");
  if (!ttRows.length) return records;

  const map = new Map();
  const emailMap = new Map();
  for (const r of ttRows) {
    const sale = r.rawData || {};
    const aliases = tigerTechRowMatchClientAliases(r);
    const oid = String(r.order_id ?? "").trim();
    const mid = String(sale.merchant_transaction_id ?? "").trim();
    const em = (r.customer_email || sale.email || "").trim().toLowerCase();
    for (const client of aliases) {
      if (oid) {
        const key = `${client}::${oid}`;
        if (!map.has(key)) map.set(key, sale);
      }
      if (mid) {
        const keyMid = `${client}::${mid}`;
        if (!map.has(keyMid)) map.set(keyMid, sale);
      }
      if (em && !em.includes("noemail")) {
        const keyEm = `${client}::${em}`;
        if (!emailMap.has(keyEm)) emailMap.set(keyEm, sale);
      }
    }
  }

  return records.map((r) => {
    if (r.source !== "Sublytics" && r.source !== "Wordpress") return r;
    if (r.source === "Sublytics" && r.matched_tigertech && r.rawData?.respective_tiger_tech_order) {
      return r;
    }
    const oid = String(r.order_id ?? "").trim();
    const tid = String(r.transaction_id ?? "").trim();
    const em = (r.customer_email || "").trim().toLowerCase();
    const aliases = subOrderMatchClientAliases(r);
    let tt = null;
    for (const client of aliases) {
      if (oid) {
        tt = map.get(`${client}::${oid}`);
        if (tt) break;
      }
    }
    if (!tt && tid) {
      for (const client of aliases) {
        tt = map.get(`${client}::${tid}`);
        if (tt) break;
      }
    }
    if (!tt && em && !em.includes("noemail")) {
      for (const client of aliases) {
        tt = emailMap.get(`${client}::${em}`);
        if (tt) break;
      }
    }
    if (!tt) return r;
    return {
      ...r,
      matched_tigertech: true,
      rawData: { ...r.rawData, respective_tiger_tech_order: tt },
    };
  });
}

/**
 * Normalize a WordPress order to the unified sales record shape.
 */
export const normalizeWordPressOrder = (order, brandNameMap = {}) => {
  const total = parseFloat(order.total || 0);

  const client =
    resolveDashboardBrandClient(order, brandNameMap) ||
    pickWordPressLineClient(order, Object.values(brandNameMap)[0] || "");

  const dateVal = order.date_created || order.date_created_gmt || "";

  return {
    _id: `wordpress-${order.order_id || order.id}`,
    source: "Wordpress",
    client,
    order_id: String(order.order_id || order.id),
    transaction_id: order.transaction_id || "",
    customer_email: order.billing?.email || order.customer_email || "",
    customer_name: order.customer_name || `${order.billing?.first_name || ""} ${order.billing?.last_name || ""}`.trim(),
    amount: total,
    status: mapWordPressStatus(order.status),
    date: dateVal
      ? dayjs(dateVal).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: dateVal,
    matched_tigertech: false,
    matched_gateway:
      relCount(order.real_auth_transactions) +
        relCount(order.real_stripe_transactions) +
        relCount(order.real_nmi_transactions) >
      0,
    platform: "Wordpress",
    rawData: order,
  };
};

/**
 * Normalize a Stripe transaction to the unified sales record shape.
 */
export const normalizeStripeTransaction = (txn, brandNameMap = {}) => {
  const amountDollars = (txn.amount || 0) / 100;
  const rawName = typeof txn.brand_name === "string" ? txn.brand_name.trim() : "";
  const client =
    resolveDashboardBrandClient(txn, brandNameMap) ||
    (rawName && !/^notfound$/i.test(rawName) ? rawName : "") ||
    "";

  const billing = txn.billing_details || {};
  const email = billing.email || txn.receipt_email || "";
  const name = billing.name || "";

  const meta = txn.metadata || {};

  return {
    _id: `stripe-${txn.id}`,
    source: "Stripe",
    client,
    order_id: txn.order_id || meta.order_id || txn.charge_id || txn.id,
    transaction_id: txn.id,
    customer_email: email || meta.customer_email || "",
    customer_name: name || meta.customer_name || "",
    amount: amountDollars,
    status: mapStripeStatus(txn.status),
    date: txn.created_date
      ? dayjs(txn.created_date).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: txn.created_date || "",
    matched_tigertech: false,
    matched_gateway: true,
    platform: "Stripe",
    rawData: txn,
  };
};

/**
 * Determine AUTH.NET #1 vs #2 from the backend-computed gateway_index.
 * gateway_index=1 → first credential (by DB id) for the brand → AUTH.NET #1
 * gateway_index=2 → second credential → AUTH.NET #2
 * Brands with a single gateway always get AUTH.NET #1.
 */
function resolveAuthNetSource(txn) {
  const idx = txn.gateway_index;
  return idx === 2 ? "AUTH.NET #2" : "AUTH.NET #1";
}

/**
 * Normalize an Auth.NET transaction to the unified sales record shape.
 */
export const normalizeAuthNetTransaction = (txn, brandNameMap = {}) => {
  const source = resolveAuthNetSource(txn);
  const rawName = typeof txn.brand_name === "string" ? txn.brand_name.trim() : "";
  const client =
    resolveDashboardBrandClient(txn, brandNameMap) ||
    (rawName && !/^notfound$/i.test(rawName) ? rawName : "") ||
    (typeof txn.gateway_account_name === "string" && txn.gateway_account_name.trim()) ||
    "";

  return {
    _id: `authnet-${txn.transId}`,
    source,
    client,
    order_id: txn.invoiceNumber || txn.transId,
    transaction_id: txn.transId,
    customer_email: txn.transaction_details?.customer?.email || txn.email || "",
    customer_name: `${txn.firstName || ""} ${txn.lastName || ""}`.trim(),
    amount: parseFloat(txn.settleAmount || 0),
    status: mapAuthNetStatus(txn.transactionStatus),
    date: txn.submitTimeLocal
      ? dayjs(txn.submitTimeLocal).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: txn.submitTimeLocal || "",
    matched_tigertech: false,
    matched_gateway: true,
    platform: source,
    rawData: txn,
  };
};

/**
 * Normalize an NMI Payarc transaction to the unified sales record shape.
 */
export const normalizeNMITransaction = (txn, brandNameMap = {}) => {
  const rawName = typeof txn.brand_name === "string" ? txn.brand_name.trim() : "";
  const client =
    resolveDashboardBrandClient(txn, brandNameMap) ||
    (rawName && !/^notfound$/i.test(rawName) ? rawName : "") ||
    (typeof txn.gateway_account_name === "string" && txn.gateway_account_name.trim()) ||
    "";

  return {
    _id: `nmi-${txn.transaction_id || txn.id}`,
    source: "NMI Payarc",
    client,
    order_id: txn.order_id || txn.transaction_id || String(txn.id),
    transaction_id: txn.transaction_id || String(txn.id),
    customer_email: txn.email || "",
    customer_name: `${txn.first_name || ""} ${txn.last_name || ""}`.trim(),
    amount: parseFloat(txn.action_amount || 0),
    status: mapNMIStatus(txn.condition),
    date: txn.action_date
      ? dayjs(txn.action_date).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: txn.action_date || "",
    matched_tigertech: false,
    matched_gateway: true,
    platform: "NMI Payarc",
    rawData: txn,
  };
};

/**
 * Map Reliable Dashboard status to a unified label.
 */
function mapReliableStatus(s) {
  const st = String(s || "").toLowerCase();
  if (st === "approved") return "Settled";
  if (st === "declined" || st === "decline") return "Declined";
  if (st === "error") return "Declined";
  if (st === "inprogress") return "Pending";
  return "Unknown";
}

/**
 * Normalize a Reliable Dashboard (Check Gateway) transaction to the unified sales record shape.
 */
export const normalizeReliableTransaction = (txn, brandNameMap = {}) => {
  const rawName = typeof txn.brand_name === "string" ? txn.brand_name.trim() : "";
  const client =
    resolveDashboardBrandClient(txn, brandNameMap) ||
    (rawName && !/^notfound$/i.test(rawName) ? rawName : "") ||
    (typeof txn.gateway_account_name === "string" && txn.gateway_account_name.trim()) ||
    "";

  return {
    _id: `reliable-${txn.remote_id || txn.id}`,
    source: "Reliable Solutions",
    client,
    order_id: txn.order_id || txn.external_id || String(txn.remote_id || txn.id),
    transaction_id: String(txn.remote_id || txn.id),
    customer_email: txn.contact_email || "",
    customer_name: txn.contact_name || txn.card_name || "",
    amount: parseFloat(txn.amount || 0),
    status: mapReliableStatus(txn.status),
    date: txn.created_on
      ? dayjs(txn.created_on).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: txn.created_on || "",
    matched_tigertech: false,
    matched_gateway: true,
    platform: "Reliable Solutions",
    rawData: txn,
  };
};

/**
 * Normalize a TigerTech sale to the unified sales record shape.
 */
export const normalizeTigerTechSale = (sale, brandNameMap = {}, source = "TigerTech") => {
  const prices = [
    parseFloat(sale.price01 || 0),
    parseFloat(sale.price02 || 0),
    parseFloat(sale.price03 || 0),
    parseFloat(sale.price04 || 0),
    parseFloat(sale.price05 || 0),
  ];
  const totalAmount = prices.reduce((sum, p) => sum + p, 0);

  const ttCustomClient = pickTigerTechDisplayClient(sale, "");
  const knownBrands = Object.values(brandNameMap);
  const client =
    (ttCustomClient && knownBrands.some((b) => b.toLowerCase() === ttCustomClient.toLowerCase()))
      ? ttCustomClient
      : (resolveDashboardBrandClient(sale, brandNameMap) || ttCustomClient || "Unknown");

  const src = source || sale.source || "TigerTech";
  const idPrefix = src.toLowerCase().replace(/[^a-z0-9]/g, "") || "tigertech";

  return {
    _id: `${idPrefix}-${sale.id}`,
    source: src,
    client,
    order_id: sale.order_number || String(sale.id),
    transaction_id: sale.merchant_transaction_id || "",
    customer_email: sale.email || "",
    customer_name: `${sale.bill_first_name || ""} ${sale.bill_last_name || ""}`.trim(),
    amount: totalAmount,
    status: "Processed",
    date: sale.order_date
      ? dayjs(sale.order_date).format("YYYY-MM-DD HH:mm")
      : "",
    raw_date: sale.order_date || "",
    matched_tigertech: src === "TigerTech",
    matched_gateway: false,
    platform: src,
    rawData: sale,
  };
};

/** NexRep uses the same positional TigerTech/Socrate file layout.
 *  Nutrialliance / OLCC CSVs put freight in `shipping` (col 67); the daily
 *  report must include it or we undercount vs the NexRep portal. */
export const normalizeNexRepSale = (sale, brandNameMap = {}) => {
  const rec = normalizeTigerTechSale(sale, brandNameMap, "NexRep");
  rec.amount = (rec.amount || 0) + (parseFloat(sale.shipping || 0) || 0);
  return rec;
};

/**
 * RPM (call center) phone order. The backend already groups OLX_Detail line
 * items into one object per Orderid and sums the line totals, so `order_total`
 * is the true order amount. RPM's own Email is a @NOEMAIL.COM placeholder; the
 * real email lives in that brand's Sublytics order (reconciliation, TBD).
 */
export const normalizeRPMOrder = (order, brandNameMap = {}) => {
  const brand = order.brand_name || order.client_raw || "Unknown";
  return {
    _id: `rpm-${order.order_id}`,
    source: "RPM",
    client: brand,
    order_id: order.order_id || "",
    transaction_id: "",
    customer_email: order.customer_email || "",
    customer_name: order.customer_name || "",
    amount: parseFloat(order.order_total || 0),
    status: "Processed",
    date: order.raw_date ? dayjs(order.raw_date).format("YYYY-MM-DD HH:mm") : "",
    raw_date: order.raw_date || "",
    matched_tigertech: false,
    matched_gateway: false,
    platform: "RPM",
    rawData: order,
  };
};

const RPM_MATCH_DATE_TOLERANCE_HOURS = 72;

/**
 * Reconcile RPM (call-center phone) orders against Sublytics / WordPress orders.
 *
 * RPM shares no transaction/order id with Sublytics, so we match on **customer
 * identity + price**: email+amount first, then name+amount (RPM emails are often
 * a @NOEMAIL.COM placeholder or slightly off, so name is the reliable fallback).
 * Matching is scoped to the same brand (client) and a ±72h window, and each
 * Sublytics order is claimed at most once to avoid many-RPM→one-Sublytics inflation.
 *
 * Sets on each RPM row: `matched_sublytics`, `_matched_sub_order_id`,
 * `_matched_sub_source`, `_rpm_sub_match_reason`.
 */
export function enrichRPMWithSublyticsMatches(records) {
  if (!records?.length) return records;
  const rpmRows = records.filter((r) => r.source === "RPM");
  if (!rpmRows.length) return records;
  const subRows = records.filter((r) => r.source === "Sublytics" || r.source === "Wordpress");
  if (!subRows.length) return records;

  const byEmailAmt = new Map(); // client::email::amount -> [sub]
  const byNameAmt = new Map();  // client::name::amount  -> [sub]
  const push = (map, key, val) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(val);
  };
  for (const s of subRows) {
    const c = normName(s.client);
    const em = normEmail(s.customer_email);
    const nm = normName(s.customer_name);
    const amt = normAmount(s.amount);
    if (!isPlaceholderEmail(em)) push(byEmailAmt, `${c}::${em}::${amt}`, s);
    if (nm) push(byNameAmt, `${c}::${nm}::${amt}`, s);
  }

  const claimed = new Set();
  const claim = (candidates, rDate) => {
    let avail = candidates.filter((s) => !claimed.has(s._id));
    if (!avail.length) return null;
    if (avail.length > 1 && rDate) {
      avail = [...avail].sort((a, b) => {
        const da = normDate(a.raw_date), db = normDate(b.raw_date);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return Math.abs(rDate.diff(da, "minute")) - Math.abs(rDate.diff(db, "minute"));
      });
    }
    return avail[0];
  };

  const matchFor = (r) => {
    const c = normName(r.client);
    const em = normEmail(r.customer_email);
    const nm = normName(r.customer_name);
    const amt = normAmount(r.amount);
    const rDate = normDate(r.raw_date);
    const withinWindow = (s) => {
      if (!rDate) return true;
      const sd = normDate(s.raw_date);
      if (!sd) return true;
      return Math.abs(rDate.diff(sd, "hour")) <= RPM_MATCH_DATE_TOLERANCE_HOURS;
    };
    // Priority 1: email + amount
    if (!isPlaceholderEmail(em)) {
      const s = claim((byEmailAmt.get(`${c}::${em}::${amt}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "email_amount" };
    }
    // Priority 2: name + amount
    if (nm) {
      const s = claim((byNameAmt.get(`${c}::${nm}::${amt}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "name_amount" };
    }
    return null;
  };

  // Deterministic: earliest RPM orders claim first.
  const rpmSorted = [...rpmRows].sort((a, b) => {
    const da = a.raw_date ? new Date(a.raw_date) : 0;
    const db = b.raw_date ? new Date(b.raw_date) : 0;
    return da - db;
  });
  const matchById = new Map();
  for (const r of rpmSorted) {
    const m = matchFor(r);
    if (m) {
      claimed.add(m.s._id);
      matchById.set(r._id, m);
    }
  }

  return records.map((r) => {
    if (r.source !== "RPM") return r;
    const m = matchById.get(r._id);
    if (!m) return { ...r, matched_sublytics: false };
    return {
      ...r,
      matched_sublytics: true,
      _matched_sub_order_id: m.s.order_id,
      _matched_sub_source: m.s.source,
      _rpm_sub_match_reason: m.reason,
    };
  });
}

const NEXREP_MATCH_DATE_TOLERANCE_HOURS = 72;

/**
 * Reconcile NexRep call-center sales against Sublytics / WordPress.
 * File layout matches TigerTech, so we try order/txn id first, then email+amount,
 * then name+amount. Same brand, ±72h, each Sublytics order claimed once.
 */
export function enrichNexRepWithSublyticsMatches(records) {
  if (!records?.length) return records;
  const nxRows = records.filter((r) => r.source === "NexRep");
  if (!nxRows.length) return records;
  const subRows = records.filter((r) => r.source === "Sublytics" || r.source === "Wordpress");
  if (!subRows.length) return records;

  const byOrderId = new Map();
  const byTxnId = new Map();
  const byEmailAmt = new Map();
  const byNameAmt = new Map();
  const push = (map, key, val) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(val);
  };
  for (const s of subRows) {
    const c = normName(s.client);
    const oid = normOrderId(s.order_id);
    const tid = normTxnId(s.transaction_id);
    const em = normEmail(s.customer_email);
    const nm = normName(s.customer_name);
    const amt = normAmount(s.amount);
    if (oid) push(byOrderId, `${c}::${oid}`, s);
    if (tid) push(byTxnId, `${c}::${tid}`, s);
    if (!isPlaceholderEmail(em)) push(byEmailAmt, `${c}::${em}::${amt}`, s);
    if (nm) push(byNameAmt, `${c}::${nm}::${amt}`, s);
  }

  const claimed = new Set();
  const claim = (candidates, rDate) => {
    let avail = candidates.filter((s) => !claimed.has(s._id));
    if (!avail.length) return null;
    if (avail.length > 1 && rDate) {
      avail = [...avail].sort((a, b) => {
        const da = normDate(a.raw_date), db = normDate(b.raw_date);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return Math.abs(rDate.diff(da, "minute")) - Math.abs(rDate.diff(db, "minute"));
      });
    }
    return avail[0];
  };

  const matchFor = (r) => {
    const c = normName(r.client);
    const oid = normOrderId(r.order_id);
    const tid = normTxnId(r.transaction_id);
    const em = normEmail(r.customer_email);
    const nm = normName(r.customer_name);
    const amt = normAmount(r.amount);
    const rDate = normDate(r.raw_date);
    const withinWindow = (s) => {
      if (!rDate) return true;
      const sd = normDate(s.raw_date);
      if (!sd) return true;
      return Math.abs(rDate.diff(sd, "hour")) <= NEXREP_MATCH_DATE_TOLERANCE_HOURS;
    };
    if (oid) {
      const s = claim((byOrderId.get(`${c}::${oid}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "order_id" };
    }
    if (tid) {
      const s = claim((byTxnId.get(`${c}::${tid}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "transaction_id" };
    }
    if (!isPlaceholderEmail(em)) {
      const s = claim((byEmailAmt.get(`${c}::${em}::${amt}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "email_amount" };
    }
    if (nm) {
      const s = claim((byNameAmt.get(`${c}::${nm}::${amt}`) || []).filter(withinWindow), rDate);
      if (s) return { s, reason: "name_amount" };
    }
    return null;
  };

  const nxSorted = [...nxRows].sort((a, b) => {
    const da = a.raw_date ? new Date(a.raw_date) : 0;
    const db = b.raw_date ? new Date(b.raw_date) : 0;
    return da - db;
  });
  const matchById = new Map();
  for (const r of nxSorted) {
    const m = matchFor(r);
    if (m) {
      claimed.add(m.s._id);
      matchById.set(r._id, m);
    }
  }

  return records.map((r) => {
    if (r.source !== "NexRep") return r;
    const m = matchById.get(r._id);
    if (!m) return { ...r, matched_sublytics: false };
    return {
      ...r,
      matched_sublytics: true,
      _matched_sub_order_id: m.s.order_id,
      _matched_sub_source: m.s.source,
      _nexrep_sub_match_reason: m.reason,
    };
  });
}

const GATEWAY_FOR_TT_MATCH = new Set(["NMI Payarc", "AUTH.NET #1", "AUTH.NET #2", "Stripe", "Elavon"]);

/**
 * Link payment-gateway rows ↔ TigerTech and Sublytics/WordPress ↔ NMI only when **brand (client)
 * and IDs align** — same transaction id and/or order id keys. No email/amount/date guessing (avoids
 * false positives across unrelated brands or different orders for the same customer).
 */
export function enrichSalesCrossSourceHints(records) {
  if (!records?.length) return records;

  const ttRows = records.filter((r) => r.source === "TigerTech");
  const txnToTt = new Map();
  const orderToTt = new Map();
  for (const r of ttRows) {
    const sale = r.rawData || {};
    const mid = String(sale.merchant_transaction_id ?? r.transaction_id ?? "").trim().toLowerCase();
    const on = String(sale.order_number ?? r.order_id ?? "").trim().toLowerCase();
    for (const cRaw of tigerTechRowMatchClientAliases(r)) {
      const c = String(cRaw || "").trim();
      if (!c) continue;
      if (mid) {
        const k = `${c}::${mid}`;
        if (!txnToTt.has(k)) txnToTt.set(k, r);
      }
      if (on) {
        const ko = `${c}::${on}`;
        if (!orderToTt.has(ko)) orderToTt.set(ko, r);
      }
    }
  }

  function findTigerTechStrict(gw) {
    const t = String(gw.transaction_id ?? "").trim().toLowerCase();
    const o = String(gw.order_id ?? "").trim().toLowerCase();
    const txn = gw.rawData || {};
    const gwClients = [
      (gw.client || "").trim(),
      (typeof txn.brand_name === "string" && txn.brand_name.trim()) || "",
    ].filter(Boolean);
    const uniq = [...new Set(gwClients)];
    for (const c of uniq) {
      if (t && txnToTt.has(`${c}::${t}`)) return txnToTt.get(`${c}::${t}`);
      if (o && orderToTt.has(`${c}::${o}`)) return orderToTt.get(`${c}::${o}`);
      if (t && orderToTt.has(`${c}::${t}`)) return orderToTt.get(`${c}::${t}`);
    }
    return null;
  }

  let out = records.map((r) => ({ ...r, rawData: { ...(r.rawData || {}) } }));

  out = out.map((r) => {
    if (!GATEWAY_FOR_TT_MATCH.has(r.source)) return r;
    if (r.matched_tigertech) return r;
    const tt = findTigerTechStrict(r);
    if (!tt) return r;
    return {
      ...r,
      matched_tigertech: true,
      rawData: {
        ...r.rawData,
        _tigertech_cross_hint: tt.rawData,
        _tigertech_match_row_id: tt._id,
      },
    };
  });

  const ttIdsLinked = new Set();
  out.forEach((r) => {
    if (GATEWAY_FOR_TT_MATCH.has(r.source) && r.matched_tigertech && r.rawData?._tigertech_match_row_id) {
      ttIdsLinked.add(r.rawData._tigertech_match_row_id);
    }
  });

  out = out.map((r) => {
    if (r.source !== "TigerTech") return r;
    if (r.matched_gateway) return r;
    if (!ttIdsLinked.has(r._id)) return r;
    return { ...r, matched_gateway: true };
  });

  const nmiRows = out.filter((r) => r.source === "NMI Payarc");

  function linkOrderToNmi(r) {
    const raw = r.rawData || {};
    if ((raw.real_nmi_transactions?.length || 0) > 0) return r;
    if (raw._dashboard_linked_nmi) return r;
    const c = r.client || "";
    const st = String(r.transaction_id ?? "").trim().toLowerCase();
    const so = String(r.order_id ?? "").trim().toLowerCase();
    for (const nmi of nmiRows) {
      if ((nmi.client || "") !== c) continue;
      const nt = String(nmi.transaction_id ?? "").trim().toLowerCase();
      const no = String(nmi.order_id ?? "").trim().toLowerCase();
      const idMatch =
        (st && nt && st === nt) ||
        (so && no && so === no) ||
        (st && no && st === no) ||
        (so && nt && so === nt);
      if (!idMatch) continue;
      return {
        ...r,
        matched_gateway: true,
        rawData: {
          ...r.rawData,
          _dashboard_linked_nmi: true,
          _dashboard_nmi_hints: [nmi.rawData],
        },
      };
    }
    return r;
  }

  out = out.map((r) => {
    if (r.source !== "Sublytics" && r.source !== "Wordpress") return r;
    return linkOrderToNmi(r);
  });

  return out;
}

// --- Status mapping helpers ---

const mapWordPressStatus = (status) => {
  const map = {
    completed: "Settled",
    processing: "Pending",
    "on-hold": "Pending",
    pending: "Pending",
    cancelled: "Declined",
    refunded: "Refunded",
    failed: "Declined",
    "checkout-draft": "Pending",
    trash: "Declined",
  };
  const normalized = status?.toLowerCase()?.replace(/[^a-z-]/g, "") || "";
  if (normalized.includes("error") || normalized.includes("fail")) return "Declined";
  return map[normalized] || "Unknown";
};

const mapStripeStatus = (status) => {
  const map = {
    succeeded: "Settled",
    paid: "Settled",
    available: "Settled",
    pending: "Pending",
    failed: "Declined",
    refunded: "Refunded",
  };
  return map[status?.toLowerCase()] || status || "Unknown";
};

const mapAuthNetStatus = (status) => {
  const map = {
    settledSuccessfully: "Settled",
    capturedPendingSettlement: "Pending",
    authorizedPendingCapture: "Pending",
    declined: "Declined",
    voided: "Declined",
    refundSettledSuccessfully: "Refunded",
    refundPendingSettlement: "Refunded",
    FDSPendingReview: "Pending",
    FDSAuthorizedPendingReview: "Pending",
  };
  return map[status] || status || "Unknown";
};

const mapNMIStatus = (condition) => {
  const map = {
    complete: "Settled",
    pending: "Pending",
    pendingsettlement: "Pending",
    failed: "Declined",
    canceled: "Declined",
    in_progress: "Pending",
  };
  return map[condition?.toLowerCase()] || condition || "Unknown";
};

// --- Summary calculation helpers ---

/**
 * Calculate summary statistics from an array of normalized sales records.
 */
export const calculateSalesSummary = (records) => {
  const summary = {
    totalOrders: records.length,
    totalRevenue: 0,
    bySource: {},
    byStatus: {},
    byClient: {},
  };

  records.forEach((r) => {
    summary.totalRevenue += r.amount || 0;

    // By source
    if (!summary.bySource[r.source]) {
      summary.bySource[r.source] = { count: 0, revenue: 0 };
    }
    summary.bySource[r.source].count++;
    summary.bySource[r.source].revenue += r.amount || 0;

    // By status
    if (!summary.byStatus[r.status]) {
      summary.byStatus[r.status] = { count: 0, revenue: 0 };
    }
    summary.byStatus[r.status].count++;
    summary.byStatus[r.status].revenue += r.amount || 0;

    // By client
    if (!summary.byClient[r.client]) {
      summary.byClient[r.client] = { count: 0, revenue: 0 };
    }
    summary.byClient[r.client].count++;
    summary.byClient[r.client].revenue += r.amount || 0;
  });

  return summary;
};

/**
 * Format currency value.
 */
export const formatCurrency = (value) => {
  return `$${(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatCompactCurrency = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

/**
 * Normalize an Elavon payment transaction to the unified sales record shape.
 */
export const normalizeElavonTransaction = (txn, brandNameMap = {}) => {
  const rawBiz = typeof txn.business_name === "string" ? txn.business_name.trim() : "";
  const client =
    resolveDashboardBrandClient(txn, brandNameMap) ||
    rawBiz ||
    "Unknown";

  const amt = parseFloat(txn.transaction_amount || 0);

  return {
    _id: `elavon-${txn.id}`,
    source: "Elavon",
    client,
    order_id: txn.payment_number || String(txn.id),
    transaction_id: txn.payment_number || "",
    customer_email: "",
    customer_name: "",
    amount: amt,
    status: txn.payment_type === "Deposit" ? "Settled" : (txn.payment_type || "Unknown"),
    date: txn.payment_date || txn.transaction_date || "",
    raw_date: txn.payment_date || txn.transaction_date || "",
    matched_tigertech: false,
    matched_gateway: true,
    platform: "Elavon",
    rawData: txn,
  };
};

/**
 * Source color mapping for tags/charts.
 */
export const SOURCE_COLORS = {
  Sublytics: "#1890ff",
  Wordpress: "#52c41a",
  "AUTH.NET #1": "#fa8c16",
  "AUTH.NET #2": "#d48806",
  "NMI Payarc": "#eb2f96",
  Check: "#8c8c8c",
  "Reliable Solutions": "#8c8c8c",
  Cash: "#389e0d",
  "Money Order": "#d4380d",
  TigerTech: "#13c2c2",
  Stripe: "#722ed1",
  Elavon: "#1d4ed8",
  RPM: "#08979c",
  NexRep: "#531dab",
};

/**
 * Status color mapping for tags.
 */
export const STATUS_COLORS = {
  Settled: "green",
  Pending: "orange",
  Declined: "red",
  Refunded: "blue",
  Captured: "green",
  Processed: "cyan",
  "In Stock": "green",
  "Low Stock": "orange",
  "Out of Stock": "red",
  Unknown: "default",
};
